import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { Panel } from '../../../../../components/atoms/Panel';
import { StatusBadge } from '../../../../../components/atoms/StatusBadge';

export interface PreviousJob {
  id: number;
  serviceStatus?: string;
  createdAt?: string;
  solutionAction?: string;
}

export interface PreviousJobsPanelProps {
  jobs: PreviousJob[];
  loading?: boolean;
  serialNumber?: string;
}

const cellSx = { fontSize: '12px', py: 0.75, px: 1.5 };
const headerSx = {
  ...cellSx,
  fontWeight: 600,
  color: 'text.secondary',
  fontSize: '11px',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.03em',
};

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, { dateStyle: 'medium' });
  } catch {
    return '-';
  }
};

export const PreviousJobsPanel: React.FC<PreviousJobsPanelProps> = ({
  jobs,
  loading,
  serialNumber,
}) => {
  const title = serialNumber ? `Previous Jobs — ${serialNumber}` : 'Previous Jobs';

  return (
    <Panel title={title}>
      {loading ? (
        <Typography sx={{ fontSize: '13px', color: 'text.secondary', py: 1.5, textAlign: 'center' }}>
          Loading...
        </Typography>
      ) : jobs.length === 0 ? (
        <Typography sx={{ fontSize: '13px', color: 'text.secondary', py: 1.5, textAlign: 'center' }}>
          No previous jobs found for this serial
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerSx}>Job ID</TableCell>
                <TableCell sx={headerSx}>Status</TableCell>
                <TableCell sx={headerSx}>Solution</TableCell>
                <TableCell sx={headerSx}>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job, idx) => (
                <TableRow
                  key={job.id}
                  sx={{
                    bgcolor: idx % 2 === 0 ? 'transparent' : 'v4.tableEven',
                    '&:hover': { bgcolor: 'v4.tableHover' },
                  }}
                >
                  <TableCell sx={cellSx}>
                    <MuiLink
                      component={RouterLink}
                      to={`/services/${job.id}`}
                      sx={{ fontSize: '12px', fontWeight: 500 }}
                      underline="hover"
                    >
                      {job.id}
                    </MuiLink>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <StatusBadge status={job.serviceStatus ?? '-'} size="small" />
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Box sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {job.solutionAction ?? '-'}
                    </Box>
                  </TableCell>
                  <TableCell sx={cellSx}>{formatDate(job.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Panel>
  );
};
